/* eslint-disable quote-props */
import { ApolloClient, ApolloLink, ApolloProvider, InMemoryCache } from "@apollo/client";
import { ERRORS_GLOBAL_MAP, ERRORS_SESSIONS_MAP } from "../config/errors-map";
import React, { useMemo } from "react";
import { User, signInWithCustomToken } from "firebase/auth";

import { GraphQLWsLink } from "@apollo/client/link/subscriptions";
import { LOCALSTORAGE_KEY } from "../config/params";
import { REFRESH_TOKEN } from "../services/refresh-token/refresh-token.query";
import { auth } from "./firebaseAuth";
import { createClient } from "graphql-ws";
import { createUploadLink } from "apollo-upload-client";
import { getMainDefinition } from "@apollo/client/utilities";
import { isWithinResumeGrace } from "../hooks/useNetworkStatus";
import { onError } from "@apollo/client/link/error";
import { setContext } from "apollo-link-context";
import { split } from "@apollo/client";
import { toastMessageActions } from "../state/request";
import { useDispatch } from "react-redux";
import { useLogout } from "../services/user/user.service";
import { userActions } from "../state/user";

const url = process.env.REACT_APP_API!;
const TOKEN_REFRESH_THRESHOLD = 50 * 60;
const PHONE_ALREADY_EXISTS_MESSAGE = "Phone number already exists!";

/**
 * Module-level promise cache for token refresh.
 * Prevents race conditions when multiple GraphQL requests
 * trigger token refresh simultaneously.
 */
let refreshTokenPromise: Promise<string> | null = null;

async function refreshAuthToken(token: any, dispatch: any): Promise<string> {
  if (refreshTokenPromise) {
    return refreshTokenPromise;
  }

  refreshTokenPromise = (async () => {
    try {
      const refreshTokenRequestHeaders = {
        refresh_token: `${token?.refresh_token}`,
        "Content-Type": "application/json",
      };

      const response = await fetch(url, {
        method: "POST",
        headers: refreshTokenRequestHeaders,
        body: JSON.stringify({
          query: REFRESH_TOKEN,
        }),
      });

      const tokenRefreshResponse = await response.json();
      const refreshedCustomToken = tokenRefreshResponse?.data?.createCustomToken?.custom_token;

      dispatch(
        userActions.setTokenNew({
          ...token,
          tokenIssuedTime: Math.floor(Date.now() / 1000),
          custom_token: refreshedCustomToken,
        }),
      );

      const userCredential = await signInWithCustomToken(auth, refreshedCustomToken);
      const user: User | null = userCredential.user;
      const idToken = await user?.getIdToken();

      return idToken;
    } catch (error) {
      console.error("[Auth] Token refresh error:", error);
      throw error;
    } finally {
      refreshTokenPromise = null;
    }
  })();

  return refreshTokenPromise;
}

const uploadLink = createUploadLink({
  uri: process.env.REACT_APP_API!,
});

const wsLink = new GraphQLWsLink(
  createClient({
    url: process.env.REACT_APP_API_WSS!,
    lazy: true,
  }),
);

const AuthApolloProvider = ({ children }: { children: React.ReactNode }) => {
  const dispatch = useDispatch();
  const { logout } = useLogout();

  const apolloClient = useMemo(() => {
    const authLink = setContext(async () => {
      const headers: any = {};
      const dataStorage = JSON.parse(localStorage.getItem(LOCALSTORAGE_KEY) || "{}");
      const tokenData = dataStorage?.user?.tokenNew;
      const tokenIssueTiming = dataStorage?.user?.tokenNew?.tokenIssuedTime;
      const currentTime = Math.floor(Date.now() / 1000);
      const tokenAge = currentTime - tokenIssueTiming;

      try {
        if (tokenData?.custom_token) {
          if (tokenAge >= TOKEN_REFRESH_THRESHOLD) {
            // Use deduplicated refresh function to prevent race conditions
            const idToken = await refreshAuthToken(tokenData, dispatch);
            headers["Authorization"] = `Bearer ${idToken}`;
          } else {
            let user: User | null = auth.currentUser;

            if (!user) {
              const userCredential = await signInWithCustomToken(auth, tokenData?.custom_token);
              user = userCredential.user;
            }

            const idToken = await user?.getIdToken();
            headers["Authorization"] = `Bearer ${idToken}`;
          }
        }

        return {
          headers,
        };
      } catch (err) {
        logout();
      }
    });

    const errorLink = onError(({ graphQLErrors, networkError, operation }) => {
      // Operations can opt out of the global error toast and handle errors in
      // their own UI by setting `context: { suppressGlobalErrorToast: true }`.
      const suppressGlobalErrorToast = Boolean(
        operation.getContext().suppressGlobalErrorToast,
      );

      if (networkError) {
        console.error("[GraphQL Network Error]:", networkError);
        if (!isWithinResumeGrace() && !suppressGlobalErrorToast) {
          dispatch(
            toastMessageActions.setGlobalAlert({
              severity: "error",
              message: "Network connection issue. Please check your internet connection and try again.",
            }),
          );
        }
      }

      if (graphQLErrors) {
        const isSessionError = graphQLErrors.some((error) => ERRORS_SESSIONS_MAP.includes(error.message));
        const errorsMapped = graphQLErrors.map((error) => {
          const isServerError = /500|501|502/.test(error.message);

          return {
            ...error,
            message: ERRORS_GLOBAL_MAP[isServerError ? "SERVER_ERROR" : error.message] || error.message,
          };
        });

        if (
          graphQLErrors[0]?.message !== PHONE_ALREADY_EXISTS_MESSAGE &&
          !suppressGlobalErrorToast
        ) {
          dispatch(toastMessageActions.requestFailure(errorsMapped));
        }

        if (isSessionError) {
          logout();
        }
      }
    });

    const splitLink = split(
      ({ query }) => {
        const definition = getMainDefinition(query);

        return definition.kind === "OperationDefinition" && definition.operation === "subscription";
      },
      wsLink,
      (authLink as unknown as ApolloLink).concat(uploadLink),
    );

    const cache = new InMemoryCache({
      typePolicies: {
        VaccineOrTest: {
          keyFields: false,
        },
        PetSoc: {
          keyFields: ["id", "__typename"],
        },
      },
    });

    return new ApolloClient({
      link: ApolloLink.from([errorLink, splitLink]),
      cache: cache,
    });
  }, [dispatch, logout]);

  return <ApolloProvider client={apolloClient}>{children}</ApolloProvider>;
};

export default AuthApolloProvider;
